import { create } from "zustand";
import { useShallow } from "zustand/shallow";
import { AuditSearchCriteria, INITIAL_AUDIT_CRITERIA, PatientSearchCriteria } from "../types/mpi.types";

// Tipos del Store

type MpiTab = "search" | "audit" | null;

interface MpiSearchState {
    activeTab: MpiTab;
    auditCriteria: AuditSearchCriteria;
    submittedAuditCriteria: AuditSearchCriteria | null;
    hasAuditSearched: boolean;
    patientCriteria: PatientSearchCriteria | null;
    hasPatientSearched: boolean;
}

interface MpiSearchActions {
    setActiveTab: (tab: MpiTab) => void;
    setAuditCriteria: (criteria: AuditSearchCriteria) => void;
    setSubmittedAuditCriteria: (criteria: AuditSearchCriteria | null) => void;
    setHasAuditSearched: (searched: boolean) => void;
    setPatientCriteria: (criteria: PatientSearchCriteria | null) => void;
    setHasPatientSearched: (searched: boolean) => void;
    clearStore: () => void;
}

// Estado Inicial

const INITIAL_STATE: MpiSearchState = {
    activeTab: "search",
    auditCriteria: INITIAL_AUDIT_CRITERIA,
    submittedAuditCriteria: null,
    hasAuditSearched: false,
    patientCriteria: null,
    hasPatientSearched: false,
};

// Store

export const useMpiSearchStore = create<MpiSearchState & MpiSearchActions>()(
    (set) => ({
        ...INITIAL_STATE,

        setActiveTab: (tab) => set({ activeTab: tab }),
        setAuditCriteria: (criteria) => set({ auditCriteria: criteria }),
        setSubmittedAuditCriteria: (criteria) => set({ submittedAuditCriteria: criteria }),
        setHasAuditSearched: (searched) => set({ hasAuditSearched: searched }),
        setPatientCriteria: (criteria) => set({ patientCriteria: criteria }),
        setHasPatientSearched: (searched) => set({ hasPatientSearched: searched }),
        clearStore: () => set(INITIAL_STATE),
    })
);

// Selectores Atómicos

export const useMpiActiveTab = () =>
    useMpiSearchStore((s) => s.activeTab);
export const useMpiAuditCriteria = () =>
    useMpiSearchStore((s) => s.auditCriteria);
export const useMpiSubmittedAuditCriteria = () =>
    useMpiSearchStore((s) => s.submittedAuditCriteria);
export const useMpiHasAuditSearched = () =>
    useMpiSearchStore((s) => s.hasAuditSearched);
export const useMpiPatientCriteria = () =>
    useMpiSearchStore((s) => s.patientCriteria);
export const useMpiHasPatientSearched = () =>
    useMpiSearchStore((s) => s.hasPatientSearched);

export const useMpiSearchActions = () =>
    useMpiSearchStore(
        useShallow((s) => ({
            setActiveTab: s.setActiveTab,
            setAuditCriteria: s.setAuditCriteria,
            setSubmittedAuditCriteria: s.setSubmittedAuditCriteria,
            setHasAuditSearched: s.setHasAuditSearched,
            setPatientCriteria: s.setPatientCriteria,
            setHasPatientSearched: s.setHasPatientSearched,
            clearStore: s.clearStore,
        }))
    );
